import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, ShoppingCart, Users, FileText, Activity } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#000000', '#666666', '#999999', '#CCCCCC', '#E5E5E5'];

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [topPublications, setTopPublications] = useState([]);
  const [statusDistribution, setStatusDistribution] = useState([]);
  const [timeRange, setTimeRange] = useState(30);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/admin/login');
      return;
    }
    
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    const token = localStorage.getItem('token');
    
    try {
      setLoading(true);
      
      const [overviewRes, trendRes, publicationsRes, statusRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/revenue-trend?days=${timeRange}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/top-publications`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/order-status-distribution`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setOverview(overviewRes.data);
      setRevenueTrend(trendRes.data);
      setTopPublications(publicationsRes.data);
      setStatusDistribution(statusRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, trend }) => (
    <div className="border border-gray-200 p-6 bg-white">
      <div className="flex items-start justify-between mb-4">
        <div className="p-3 border border-gray-200">
          <Icon className="w-6 h-6" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-sm font-manrope ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp className="w-4 h-4" />
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div>
        <div className="font-mono text-3xl font-bold mb-1">{value}</div>
        <div className="font-manrope text-sm text-gray-600 uppercase tracking-widest">{title}</div>
        {subtitle && <div className="font-manrope text-xs text-gray-500 mt-1">{subtitle}</div>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="font-manrope text-gray-600">Loading analytics...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-playfair text-4xl font-bold mb-2">Analytics Dashboard</h1>
        <p className="font-manrope text-sm text-gray-600">Revenue, orders, and performance metrics</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={DollarSign}
          title="Total Revenue"
          value={`₹${(overview?.total_revenue || 0).toLocaleString('en-IN')}`}
          subtitle={`Avg: ₹${(overview?.avg_order_value || 0).toLocaleString('en-IN')}`}
        />
        <StatCard
          icon={ShoppingCart}
          title="Paid Orders"
          value={overview?.paid_orders || 0}
          subtitle={`${overview?.total_orders || 0} total orders`}
        />
        <StatCard
          icon={Users}
          title="Customers"
          value={overview?.total_customers || 0}
          subtitle={`${overview?.conversion_rate || 0}% conversion`}
        />
        <StatCard
          icon={FileText}
          title="Active Publications"
          value={overview?.active_publications || 0}
          subtitle="Live in marketplace"
        />
      </div>

      {/* Revenue Trend */}
      <div className="border border-gray-200 p-6 bg-white mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-playfair text-2xl font-bold">Revenue Trend</h2>
          <div className="flex gap-2">
            {[7, 30, 90].map(days => (
              <button
                key={days}
                onClick={() => setTimeRange(days)}
                className={`px-4 py-2 font-manrope text-xs uppercase tracking-widest transition-all duration-300 ${
                  timeRange === days
                    ? 'bg-black text-white'
                    : 'border border-gray-200 hover:border-black'
                }`}
              >
                {days}D
              </button>
            ))}
          </div>
        </div>
        
        {revenueTrend.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
              <XAxis 
                dataKey="date" 
                style={{ fontFamily: 'monospace', fontSize: 12 }}
                stroke="#666"
              />
              <YAxis 
                style={{ fontFamily: 'monospace', fontSize: 12 }}
                stroke="#666"
              />
              <Tooltip 
                contentStyle={{ 
                  border: '1px solid #000',
                  background: '#fff',
                  fontFamily: 'Manrope'
                }}
                formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Revenue']}
              />
              <Legend wrapperStyle={{ fontFamily: 'Manrope' }} />
              <Line 
                type="monotone" 
                dataKey="revenue" 
                stroke="#000" 
                strokeWidth={2}
                name="Revenue"
                dot={{ fill: '#000', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12">
            <p className="font-manrope text-gray-600">No revenue data for selected period</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Publications */}
        <div className="border border-gray-200 p-6 bg-white">
          <h2 className="font-playfair text-2xl font-bold mb-6">Top Publications</h2>
          
          {topPublications.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topPublications.slice(0, 5)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis 
                    dataKey="publication_name" 
                    style={{ fontFamily: 'monospace', fontSize: 10 }}
                    stroke="#666"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis 
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                    stroke="#666"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      border: '1px solid #000',
                      background: '#fff',
                      fontFamily: 'Manrope'
                    }}
                    formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="#000" />
                </BarChart>
              </ResponsiveContainer>
              
              <div className="mt-6 space-y-2">
                {topPublications.slice(0, 5).map((pub, index) => (
                  <div key={index} className="flex justify-between items-center p-3 border border-gray-200">
                    <div>
                      <div className="font-manrope font-semibold text-sm">{pub.publication_name}</div>
                      <div className="font-manrope text-xs text-gray-600">{pub.category}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-semibold text-sm">₹{pub.revenue.toLocaleString('en-IN')}</div>
                      <div className="font-manrope text-xs text-gray-600">{pub.orders} orders</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="font-manrope text-gray-600">No publication data available</p>
            </div>
          )}
        </div>

        {/* Order Status Distribution */}
        <div className="border border-gray-200 p-6 bg-white">
          <h2 className="font-playfair text-2xl font-bold mb-6">Order Status</h2>
          
          {statusDistribution.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusDistribution}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(entry) => entry.status}
                  >
                    {statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      border: '1px solid #000',
                      background: '#fff',
                      fontFamily: 'Manrope'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              
              <div className="mt-6 space-y-2">
                {statusDistribution.map((status, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4" 
                        style={{ background: COLORS[index % COLORS.length] }}
                      />
                      <span className="font-manrope text-sm capitalize">
                        {status.status.replace('_', ' ')}
                      </span>
                    </div>
                    <span className="font-mono font-semibold text-sm">{status.count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="font-manrope text-gray-600">No order status data available</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics;
